import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  PhoneIcon,
  BuildingOffice2Icon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { FadeIn } from '../components/animations/FadeIn';
import { TopographicBackground } from '../components/landing/TopographicBackground';

/**
 * "Contact Us" — the destination for the printed-marketing QR code.
 *
 * Distinct from ContactSales (a product-purchase inquiry flow). This is a
 * general "get in touch" form that posts to /api/v1/contact, which emails the
 * team via Resend and sends the requester a confirmation.
 *
 * Reachable on prod at argostellus.com/contact: the React SPA is the default
 * backend, so any non-API path renders this route. No URL-map change needed.
 *
 * Styled to match the landing (Landing.tsx): animated topographic backdrop,
 * glassy panel, Orbitron heading, cyan-glow CTA. Mobile-first — a QR scan
 * always lands on a phone.
 */

// Shared field styling — dark glass input, 16px text so iOS doesn't zoom on focus.
const inputClass =
  'w-full px-4 py-3.5 bg-white/[0.04] border border-white/15 rounded-lg text-white text-base ' +
  'placeholder:text-white/30 focus:border-cyan-400/60 focus:bg-white/[0.06] focus:outline-none ' +
  'focus:shadow-[0_0_0_3px_rgba(34,211,238,0.12)] transition-colors';

const labelClass = 'flex items-center gap-1.5 text-[10px] text-white/50 tracking-widest mb-2';

function Backdrop() {
  return (
    <div className="fixed inset-0 z-0 pointer-events-none">
      <TopographicBackground className="w-full h-full" spill />
      {/* Darken toward center so the form panel stays legible over the animation */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_45%,_transparent_0%,_rgba(0,0,0,0.55)_100%)]" />
    </div>
  );
}

export function ContactUs() {
  const [searchParams] = useSearchParams();
  // QR codes can tag their origin, e.g. /contact?source=print-2026-06.
  const source = searchParams.get('source') || 'contact_page';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/v1/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to submit. Please try again.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black relative overflow-hidden">
        <Backdrop />
        <div className="relative z-10 min-h-screen flex items-center justify-center px-5 py-16">
          <FadeIn>
            <div className="max-w-md mx-auto text-center border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-2xl p-8 sm:p-10 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
              <div className="inline-flex items-center justify-center p-4 border border-emerald-400/30 bg-emerald-500/10 rounded-full mb-6">
                <CheckCircleIcon className="h-11 w-11 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-white tracking-widest font-orbitron mb-4">
                MESSAGE SENT
              </h1>
              <p className="text-sm text-white/60 leading-relaxed mb-8">
                Thanks for reaching out. Our team will be in touch within 24 hours.
              </p>
              <Link
                to="/"
                className="inline-block w-full sm:w-auto px-8 py-3 border border-white/15 text-white text-xs tracking-widest rounded-lg hover:bg-white/5 hover:border-cyan-400/40 transition-colors"
              >
                RETURN HOME
              </Link>
            </div>
          </FadeIn>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <Backdrop />

      <div className="relative z-10">
        {/* Header — brand wordmark + back link, transparent over the backdrop */}
        <header className="border-b border-white/[0.08]">
          <div className="max-w-xl mx-auto px-5 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-baseline gap-2">
              <span className="text-base font-bold text-white tracking-[0.3em] font-orbitron">
                ARGOS
              </span>
              <span className="hidden sm:inline text-[9px] text-white/40 tracking-[0.25em] font-orbitron">
                ENVIRONMENTAL AUTOMATION
              </span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-[11px] text-white/50 tracking-wider hover:text-cyan-300 transition-colors"
            >
              <ArrowLeftIcon className="w-3.5 h-3.5" />
              HOME
            </Link>
          </div>
        </header>

        <div className="max-w-xl mx-auto px-5 py-10 sm:py-16">
          {/* Heading */}
          <FadeIn>
            <div className="text-center mb-8 sm:mb-10">
              <div className="inline-flex items-center justify-center p-3.5 border border-cyan-400/30 bg-cyan-500/10 rounded-lg mb-5 shadow-[0_0_28px_-10px_rgba(34,211,238,0.7)]">
                <EnvelopeIcon className="h-8 w-8 text-cyan-400" />
              </div>
              <p className="text-[10px] text-cyan-400 tracking-[0.4em] mb-3">GET IN TOUCH</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-wide font-orbitron mb-3">
                CONTACT US
              </h1>
              <p className="text-sm text-white/50 tracking-wide max-w-sm mx-auto leading-relaxed">
                Tell us a little about you and we&apos;ll get back to you within 24 hours.
              </p>
            </div>
          </FadeIn>

          {/* Form panel */}
          <FadeIn delay={0.1}>
            <form
              onSubmit={handleSubmit}
              className="border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-2xl p-5 sm:p-8 space-y-5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]"
            >
              {error && (
                <div className="border border-red-500/30 bg-red-500/10 rounded-lg p-3.5 text-center">
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              <div>
                <label className={labelClass}>
                  <UserIcon className="w-3 h-3" />
                  NAME <span className="text-cyan-400/80">*</span>
                </label>
                <input
                  type="text"
                  autoComplete="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={inputClass}
                  placeholder="Jane Smith"
                />
              </div>

              <div>
                <label className={labelClass}>
                  <EnvelopeIcon className="w-3 h-3" />
                  EMAIL <span className="text-cyan-400/80">*</span>
                </label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={inputClass}
                  placeholder="jane@company.com"
                />
              </div>

              {/* Company + phone: one row on >=sm, stacked on mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>
                    <BuildingOffice2Icon className="w-3 h-3" />
                    COMPANY
                  </label>
                  <input
                    type="text"
                    autoComplete="organization"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className={inputClass}
                    placeholder="Acme Environmental"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <PhoneIcon className="w-3 h-3" />
                    PHONE
                  </label>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={inputClass}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>MESSAGE</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className={`${inputClass} resize-none`}
                  placeholder="How can we help?"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg border border-cyan-400/40 bg-cyan-500/15 text-cyan-100 text-sm font-semibold tracking-[0.25em] font-orbitron shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-8px_rgba(34,211,238,0.5)] hover:bg-cyan-500/25 hover:border-cyan-400/70 hover:text-white hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_0_36px_-4px_rgba(34,211,238,0.7)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                {submitting ? 'SENDING…' : 'SEND MESSAGE'}
              </button>
            </form>
          </FadeIn>

          {/* Direct-email fallback */}
          <FadeIn delay={0.2}>
            <div className="mt-7 text-center">
              <p className="text-[10px] text-white/40 tracking-widest mb-2">PREFER EMAIL?</p>
              <a
                href="mailto:support@argostellus.com"
                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                support@argostellus.com
              </a>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
}
